import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/appToast";
import { Pagination } from "@mui/material";
import ContentContainer from "@/components/containers/contentContainer";
import { QuoteSelectionModeToggle } from "@/components/quote/QuoteSelectionModeToggle";
import { WorkerProfileCardSkeleton } from "@/utils/WorkerProfileCardSkeleton";
import { api } from "@/utils/api";
import { normalizeWorkersListResponse } from "@/vike/ssrHelpers";
import type { HomeFilterUiState } from "@/utils/homeSearchParams";
import { isAuthenticated } from "@/shared/services/credentialsService";
import useDimension from "@/hooks/use-dimension";
import RequestQuoteFormPanel, {
  REQUEST_QUOTE_FORM_ID,
} from "@/pages/hire/request-quote-compose/RequestQuoteFormPanel";
import { QuoteProviderCard } from "@/pages/hire/request-quote-compose/QuoteProviderCard";
import {
  createEmptyRequestQuoteDraft,
  patchRequestQuoteDraft,
  readRequestQuoteDraft,
  setRequestQuoteImageFiles,
  type QuoteSelectionMode,
  type RequestQuoteDraft,
  type RequestQuoteDraftForm,
  type RequestQuoteDraftProvider,
  writeRequestQuoteDraft,
} from "@/utils/requestQuoteDraft";
import {
  getProviderUserId,
  getWorkerProfileId,
} from "@/utils/workerProfileUtils";

type LocationNavState = {
  apiPayload?: Record<string, unknown>;
  ui?: HomeFilterUiState;
};

const PAGE_SIZE = 12;

const toProviderDraft = (candidate: any): RequestQuoteDraftProvider | null => {
  const userId = getProviderUserId(candidate);
  if (!userId) return null;
  return {
    userId,
    profileId: getWorkerProfileId(candidate) || "",
    name: `${candidate?.user?.first_name || ""} ${candidate?.user?.last_name || ""}`.trim(),
    avatar: typeof candidate?.user === "object" ? candidate?.user?.avatar : undefined,
  };
};

export default function RequestQuoteComposePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { width } = useDimension();
  const isMobileLayout = width > 0 && width < 768;
  const { user } = useSelector((state: any) => state.user || {});

  const navState = (location.state || {}) as LocationNavState;

  const [draft, setDraft] = useState<RequestQuoteDraft>(() => {
    const existing = readRequestQuoteDraft();
    if (existing) return existing;
    return createEmptyRequestQuoteDraft({
      apiPayload: navState.apiPayload || {},
      ui: navState.ui || {},
    });
  });

  const [workers, setWorkers] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [areaFallbackMessage, setAreaFallbackMessage] = useState<string | null>(
    null
  );
  const continueAfterSaveRef = useRef(false);
  const [stepError, setStepError] = useState<string | null>(null);
  /** Stable key so selecting providers does not refetch the listing */
  const filterKey = JSON.stringify(draft.filters.apiPayload || {});

  useEffect(() => {
    if (!isAuthenticated() || user?.type !== "user") {
      navigate(`/ingresar?redirect=${encodeURIComponent("/request-quote")}`, {
        replace: true,
      });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (navState.apiPayload && Object.keys(navState.apiPayload).length > 0) {
      const next = patchRequestQuoteDraft({
        filters: {
          apiPayload: navState.apiPayload,
          ui: navState.ui || {},
        },
      });
      setDraft(next);
    } else {
      writeRequestQuoteDraft(draft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingWorkers(true);
      setAreaFallbackMessage(null);
      try {
        const apiPayload = JSON.parse(filterKey) as Record<string, unknown>;
        const payload = {
          ...apiPayload,
          page,
          limit: PAGE_SIZE,
        };
        const result = await api.getFilterWorkersList(payload);
        if (cancelled) return;
        const normalized = normalizeWorkersListResponse(result ?? null);
        const rows = (normalized.data || []).filter(
          (c: any) => c?.user?._id !== user?._id
        );
        setWorkers(rows);
        setTotalPages(normalized.totalPages || 1);
        setAreaFallbackMessage(
          normalized.isOtherAreasFallback && normalized.emptyAreaMessage
            ? rows.length > 0
              ? `${normalized.emptyAreaMessage} ${t('otherRegionsProvidersAvailable')}`
              : normalized.emptyAreaMessage
            : null
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setWorkers([]);
          setTotalPages(1);
          setAreaFallbackMessage(null);
        }
      } finally {
        if (!cancelled) setLoadingWorkers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterKey, page, user?._id, t]);

  const mode = draft.mode;
  const selectedProviders = draft.selectedProviders;

  const setMode = (next: QuoteSelectionMode) => {
    const updated = patchRequestQuoteDraft({
      mode: next,
      selectedProviders: next === "auto" ? [] : draft.selectedProviders,
    });
    setDraft(updated);
    setStepError(null);
  };

  const toggleProvider = (candidate: any) => {
    if (mode !== "manual") return;
    const userId = getProviderUserId(candidate);
    if (!userId) {
      toast.error(t("something_went_wrong") || "Could not resolve provider id.");
      return;
    }
    if (String(userId) === String(user?._id)) {
      toast.error(
        t("cannotSendProposalToYourself") ||
          "You cannot send a proposal to yourself."
      );
      return;
    }
    const exists = selectedProviders.some((p) => p.userId === userId);
    let nextProviders: RequestQuoteDraftProvider[];
    if (exists) {
      nextProviders = selectedProviders.filter((p) => p.userId !== userId);
    } else {
      if (selectedProviders.length >= 3) {
        toast.error(t("maxProvidersSelected") || "You can select up to 3 providers.");
        return;
      }
      const draftProvider = toProviderDraft(candidate);
      if (!draftProvider) {
        toast.error(t("something_went_wrong") || "Could not resolve provider id.");
        return;
      }
      nextProviders = [...selectedProviders, draftProvider];
    }
    const updated = patchRequestQuoteDraft({ selectedProviders: nextProviders });
    setDraft(updated);
    setStepError(null);
  };

  const goToReview = (current: RequestQuoteDraft = draft) => {
    if (current.mode === "manual" && current.selectedProviders.length === 0) {
      setStepError(
        t("mustSelectProvider") || "Please select at least one provider."
      );
      document
        .querySelector("[data-provider-step-error]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (!current.form.description?.trim()) {
      if (isMobileLayout && current.mobileStep === "providers") {
        const stepped = patchRequestQuoteDraft({ mobileStep: "form" });
        setDraft(stepped);
        window.scrollTo(0, 0);
        return;
      }
      continueAfterSaveRef.current = true;
      const form = document.getElementById(
        REQUEST_QUOTE_FORM_ID
      ) as HTMLFormElement | null;
      if (form) {
        form.requestSubmit();
        return;
      }
      return;
    }
    setStepError(null);
    writeRequestQuoteDraft(current);
    navigate("/request-quote/review");
  };

  const handleFormSave = (form: RequestQuoteDraftForm, files: File[]) => {
    setRequestQuoteImageFiles(files);
    const updated = patchRequestQuoteDraft({ form });
    setDraft(updated);

    if (isMobileLayout) {
      const stepped = patchRequestQuoteDraft({ mobileStep: "providers" });
      setDraft(stepped);
      window.scrollTo(0, 0);
      return;
    }

    if (continueAfterSaveRef.current) {
      continueAfterSaveRef.current = false;
      goToReview(updated);
      return;
    }

    toast.success(
      t("quoteDetailsSaved") ||
        "Quote details saved. Choose providers, then continue."
    );
  };

  const handleDesktopContinue = () => {
    continueAfterSaveRef.current = true;
    const form = document.getElementById(
      REQUEST_QUOTE_FORM_ID
    ) as HTMLFormElement | null;
    if (form) {
      form.requestSubmit();
      return;
    }
    continueAfterSaveRef.current = false;
    goToReview();
  };

  const preferredServiceId = draft.filters.ui?.serviceId;
  const selectedCount = selectedProviders.length;
  const canContinue =
    draft.form.description?.trim() &&
    (mode === "auto" || selectedCount > 0);

  const providerGrid = (
    <>
      {areaFallbackMessage && (
        <div
          role="status"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 text-center"
        >
          {areaFallbackMessage}
        </div>
      )}
      {loadingWorkers ? (
        <div
          className={`grid gap-4 ${
            isMobileLayout ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
          }`}
        >
          {[...Array(4)].map((_, i) => (
            <WorkerProfileCardSkeleton key={i} />
          ))}
        </div>
      ) : workers.length === 0 ? (
        !areaFallbackMessage ? (
          <p className="text-sm text-[#424654] py-8 text-center">
            {t("noProfessionalsFound")}
          </p>
        ) : null
      ) : (
        <div
          className={`grid gap-4 ${
            isMobileLayout ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
          }`}
        >
          {workers.map((candidate) => {
            const providerUserId = getProviderUserId(candidate);
            const isSelected = selectedProviders.some(
              (p) => p.userId === providerUserId
            );
            return (
              <QuoteProviderCard
                key={
                  getWorkerProfileId(candidate) ||
                  providerUserId ||
                  candidate.id
                }
                candidate={candidate}
                layout={isMobileLayout ? "mobile" : "desktop"}
                selectionMode={mode}
                isSelected={isSelected}
                selectionDisabled={!isSelected && selectedProviders.length >= 3}
                onToggleSelected={toggleProvider}
              />
            );
          })}
        </div>
      )}

      <div className="flex justify-center pt-2">
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_e, p) => setPage(p)}
          color="primary"
          size="small"
        />
      </div>
    </>
  );

  /* ——— Mobile: form step ——— */
  if (isMobileLayout && draft.mobileStep === "form") {
    return (
      <ContentContainer className="!p-0 m-0 min-h-screen bg-[#f9f9ff]">
        <div className="bg-[#4285F4] min-h-screen pb-36">
          <div className="max-w-xl mx-auto flex flex-col gap-8 px-6 pt-20">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">
                {t("newServiceRequest")}
              </h1>
              <p className="text-sm text-[#ccd8ff] mt-2">
                {t("fillDetailsToFindPros")}
              </p>
              <div className="mt-4 h-1 rounded-full bg-white/30 overflow-hidden">
                <div className="h-full w-1/3 rounded-full bg-white" />
              </div>
            </div>

            <RequestQuoteFormPanel
              variant="mobile"
              hideSubmit
              initialForm={draft.form}
              preferredServiceId={preferredServiceId}
              submitLabel={t("findProfessionals")}
              onSave={handleFormSave}
            />
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white/90 backdrop-blur-md px-6 py-4 shadow-[0_-4px_24px_rgba(0,0,0,0.06)]">
          <button
            type="submit"
            form={REQUEST_QUOTE_FORM_ID}
            className="w-full h-14 rounded-xl bg-[#4285F4] text-white text-xl font-semibold flex items-center justify-center gap-3 shadow-md"
          >
            {t("findProfessionals")}
            <span aria-hidden>→</span>
          </button>
        </div>
      </ContentContainer>
    );
  }

  /* ——— Mobile: providers step ——— */
  if (isMobileLayout) {
    return (
      <ContentContainer className="!p-0 m-0 min-h-screen bg-[#f9f9ff] pb-28 pt-20">
        <div className="sticky top-20 z-10 flex items-center gap-3 px-4 h-14 bg-[#f9f9ff]/80 backdrop-blur-md shadow-sm">
          <button
            type="button"
            className="size-11 rounded-full flex items-center justify-center text-[#111c2d] text-lg"
            onClick={() => {
              const updated = patchRequestQuoteDraft({ mobileStep: "form" });
              setDraft(updated);
            }}
            aria-label={t("back")}
          >
            ←
          </button>
          <h1 className="text-xl font-semibold text-[#111c2d]">
            {t("chooseProfessionals")}
          </h1>
        </div>

        <div className="px-4 pt-6 pb-4">
          <h2 className="text-2xl font-bold text-[#111c2d] tracking-tight">
            {t("chooseProfessionals")}
          </h2>
          <p className="text-sm text-[#424654] mt-2">
            {t("selectUpTo3Experts")}
          </p>
        </div>

        <div className="px-4 mb-4">
          <QuoteSelectionModeToggle
            mode={mode}
            onChange={setMode}
            className="!mt-0"
          />
        </div>

        {stepError && (
          <div
            data-provider-step-error
            className="mx-4 mb-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            role="alert"
          >
            {stepError}
          </div>
        )}

        <div className="px-4 flex flex-col gap-3">{providerGrid}</div>

        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.05)]">
          <div className="flex items-center justify-between gap-4 max-w-xl mx-auto">
            <div>
              <p className="text-xs font-medium text-[#424654]">
                {t("selected")}
              </p>
              <p className="text-xl font-semibold text-[#4285F4]">
                {mode === "auto" ? "Auto" : `${selectedCount}/3`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => goToReview()}
              disabled={!canContinue}
              className="h-12 px-8 rounded-lg bg-[#4285F4] text-white text-xs font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
            >
              {t("continue")}
              <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </ContentContainer>
    );
  }

  /* ——— Desktop ——— */
  return (
    <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen pb-28">
      <div className="bg-[#e7eeff] px-6 py-8">
        <div className="max-w-[1440px] mx-auto">
          <h1 className="text-base text-[#111c2d]">
            {t("newServiceRequest")}
          </h1>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <div className="h-2 w-16 rounded-full bg-[#4285F4]" />
            <div className="h-2 w-16 rounded-full bg-[#d8e3fb]" />
            <div className="h-2 w-16 rounded-full bg-[#d8e3fb]" />
            <span className="pl-2 text-sm sm:text-base uppercase tracking-wider text-[#424654]">
              {t("step1Of3Details")}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 py-8 flex gap-8 items-start">
        <aside className="w-full max-w-[520px] lg:w-[480px] xl:w-[520px] shrink-0">
          <div className="bg-[#4285F4] rounded-xl p-5 shadow-sm flex flex-col gap-6 sticky top-4">
            <div className="flex items-center gap-2 border-b border-white/25 pb-3">
              <h2 className="text-base font-semibold text-white">
                {t("requestDetails")}
              </h2>
            </div>
            <RequestQuoteFormPanel
              variant="desktop"
              brandSurface
              hideSubmit
              initialForm={draft.form}
              preferredServiceId={preferredServiceId}
              submitLabel={t("saveQuoteDetails")}
              onSave={handleFormSave}
              onSaveAborted={() => {
                continueAfterSaveRef.current = false;
              }}
            />
          </div>
        </aside>

        <section className="flex-1 min-w-0 flex flex-col gap-6">
          <div className="flex min-w-0 items-center justify-between gap-3 overflow-x-auto">
            <h2 className="shrink-0 whitespace-nowrap text-base text-[#111c2d]">
              {t("availableProfessionals")}
            </h2>
            <QuoteSelectionModeToggle
              mode={mode}
              onChange={setMode}
              className="!mt-0 shrink-0"
            />
          </div>

          {mode === "manual" && (
            <p className="text-sm text-[#424654]">
              {t("selected")}:{" "}
              <span className="font-semibold text-[#4285F4]">
                {selectedCount}
              </span>
              /3
            </p>
          )}

          {stepError && (
            <div
              data-provider-step-error
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              role="alert"
            >
              {stepError}
            </div>
          )}

          {providerGrid}
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-[#d8e3fb] bg-white/90 backdrop-blur-md">
        <div className="max-w-[1440px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-full bg-[#e7eeff]" aria-hidden />
            <div>
              <p className="text-base font-semibold text-[#111c2d]">
                {t("professionalsSelected")}
              </p>
              <p className="text-sm text-[#424654]">
                {t("chooseUpTo3ForQuotes")}
              </p>
            </div>
            <p className="text-2xl font-semibold text-[#4285F4] ml-2">
              {mode === "auto" ? "Auto" : `${selectedCount}/3`}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDesktopContinue}
            className="h-12 px-8 rounded-lg bg-[#4285F4] text-white text-sm font-medium flex items-center gap-2 shadow-md"
          >
            {t("continueToReview")}
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </ContentContainer>
  );
}
